import { Injectable } from '@angular/core';
import { FontFaceModel, FontFormat, FontModel, FontType } from '../models';
import { take } from 'rxjs';
import { FontsApiService } from './fonts-api.service';

@Injectable({
  providedIn: 'root'
})
export class FontLoaderService {
  static FONT_DATA_ATTRIBUTE = 'data-lms-font-id';

  protected readonly googleFontBase = 'https://fonts.googleapis.com/css2';
  protected readonly fontStyleTagId = 'lms-font-style-sheets';
  protected readonly fontFormatOrder = ['embedded-opentype', 'woff2', 'woff', 'opentype', 'truetype', 'svg'];

  protected readonly DEFAULT_WEIGHT = ['300','400','500','600','700']

  constructor(private readonly fontsApiService: FontsApiService) { }

  public loadHtmlContentFonts(content: string): void {
    if (!content) {
      return;
    }

    const attribute = FontLoaderService.FONT_DATA_ATTRIBUTE;
    const regex = new RegExp(`${attribute}="([^"]+)"`, 'g');

    const matches = content.match(regex) || [];

    const uniqueFontIds = [...new Set(matches.map(m => m.split('"')[1]))];

    if (!uniqueFontIds.length) {
      return;
    }

    this.fontsApiService.getPlatformFonts()
      .pipe(
        take(1)
      )
      .subscribe(fonts => {
        const usedFonts = fonts.filter(font => uniqueFontIds.indexOf(font.id) !== -1);
        usedFonts.forEach(font => this.loadFont(font));
      });
  }

  public loadPlatformFont(font: FontModel): void {
    if (!font) {
      return;
    }
    
    const loaded = this.loadFont(font);

    if (!loaded) {
      return;
    }
    
    document.body.style.setProperty('--lms-base-platform-font', `"${font.title}"`);
  }

  public loadFont(font: FontModel): boolean {
    if (!font) {
      return false;
    }

    if (font.type === FontType.GOOGLE) {
      return this.loadGoogleFont(font);
    }

    return this.loadCustomFont(font);
  }

  protected injectFontTag(id: string, element: HTMLLinkElement | HTMLStyleElement): void {
    if (document.getElementById(id)) {
      return;
    }

    element.id = id;
    document.head.appendChild(element);
  }

  public loadGoogleFont(font: FontModel): boolean {
    const family = encodeURIComponent(font.title).replace(/%20/g, '+');
    const fontParam = `${family}:wght@${this.DEFAULT_WEIGHT.join(';')}`;
    const url = `${this.googleFontBase}?family=${fontParam}&display=swap`;
    const id = [this.fontStyleTagId, font.id].filter(Boolean).join('-');

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;

    this.injectFontTag(id, link);
    return true;
  }

  protected loadCustomFont(font: FontModel): boolean {
    const fontCss = this.getCustomFontCss(font);

    if (!fontCss) {
      return;
    }

    const id = [this.fontStyleTagId, font.id].filter(Boolean).join('-');
    const style = document.createElement('style');
    style.appendChild(document.createTextNode(fontCss));

    this.injectFontTag(id, style);
    return true;
  }

  protected groupByFileName(array: any[]) {
    return array.reduce((objectsByKeyValue, obj: any) => {
      if (!obj.filename) {
        const splitted = obj.uri?.split('/')?.reverse() || [];
        obj.filename = splitted[0];
      }

      const value = obj.filename.replace(/\.[^/.]+$/, '');
      const extension: keyof typeof FontFormat = obj.filename.match(/\.([^/.#]+)([#][^.]+)*$/)[1];
      obj.format = FontFormat[extension];
      objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
      return objectsByKeyValue;
    }, {});
  }

  protected getCustomFontCss(font: FontModel): string {
    const fontData = font.package;


    if (!fontData?.content?.length) {
      return '';
    }

    const grouped = this.groupByFileName(fontData.content);

    const fontItems: FontFaceModel[] = Object.keys(grouped).map(item => {
      return {
        fileList: grouped[item].sort((a, b) => this.fontFormatOrder.indexOf(a.format) > this.fontFormatOrder.indexOf(b.format) ? 1 : -1),
        weight: grouped[item][0].filename.toLowerCase().indexOf('-bold.') !== -1 ? 700 : 400,
        style: 'normal'
      };
    });

    if (!fontItems?.length) {
      return '';
    }

    return fontItems
      .map(item => this.buildFontFace(item, font.title))
      .filter(Boolean)
      .join('');
  }

  protected buildFontFace(item: FontFaceModel, fontFamily: string): string {
    const src = item.fileList
      .map(file => {
        const url = this.resolveCustomFontSource(file);
        return url ? `url('${url}')${file.format ? ` format('${file.format}')` : ''}`
          : null;
      })
      .filter(Boolean)
      .join(',');

    if (!src) {
      return '';
    }

    return `@font-face {
        font-family: "${fontFamily}";
        font-style: ${item.style};
        font-weight: ${item.weight};
        font-display: swap;
        src: ${src};
      }`;
  }

  protected resolveCustomFontSource(data: { key: string, uri: string, url: string}): string {
    return data.url || data.uri;
  }

}
